import { yupResolver } from "@hookform/resolvers/yup";
import BreadcrumbWithLink from "components/Common/BreadcrumbWithLink";
import FormHubSelect from "components/Common/Form/FormHubSelect";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import { getPhoneValidation } from "constants/validate";
import { formConvertErrorMessage, formValidate } from "helpers/form_helper";
import {
  useCreateBus,
  useDistricts,
  useListUserProfile,
  useProvinces,
  useWards,
} from "hooks";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MetaTags } from "react-meta-tags";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import { useToasts } from "react-toast-notifications";
import { Button, Card, CardBody, Col, FormGroup, Label, Row } from "reactstrap";
import { Switch } from "antd";
import * as yup from "yup";

const initLocalitiesParams = () => {
  return {
    countryId: 1,
    type: 1,
    parent_code: "",
  };
};

const CreateBus = props => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["bus"]);
  const { addToast } = useToasts();

  const [is_level_2, setIsLevel2] = useState(true);

  const [provinceParams, setProvinceParams] = useState(initLocalitiesParams);
  const [districtParams, setDistrictParams] = useState(initLocalitiesParams);
  const [wardParams, setWardParams] = useState(initLocalitiesParams);

  const getInitDefaultValues = () => {
    return {
      hub_id: null,
      name: "",
      phone_number: "",
      address: "",
      ward_code: undefined,
      district_code: undefined,
      province_code: undefined,
      mapping_sale_id: null,
    };
  };

  // Schema validation với ward conditional
  const schema = yup.object().shape({
    hub_id: yup
      .object()
      .nullable()
      .required(t("form.validate.required", { title: t("form.hub_id") })),
    name: yup
      .string()
      .required(t("form.validate.required", { title: t("form.name") }))
      .max(250, t("form.validate.char_max", { max: 250 })),
    address: yup
      .string()
      .required(t("form.validate.required", { title: t("form.address") }))
      .max(250, t("form.validate.char_max", { max: 250 })),
    province_code: yup
      .object()
      .nullable()
      .required(
        t("form.validate.required", { title: t("form.province_code") })
      ),
    district_code: yup
      .object()
      .nullable()
      .required(
        t("form.validate.required", {
          title: is_level_2 ? "Phường xã" : t("form.district_code"),
        })
      ),

    ward_code: yup.object().when([], {
      is: () => is_level_2,
      then: yup.object().nullable(),
      otherwise: yup
        .object()
        .nullable()
        .required(t("form.validate.required", { title: t("form.ward_code") })),
    }),
    phone_number: yup
      .string()
      .nullable()
      .required(t("form.validate.required", { title: t("form.phone_number") }))
      .matches(getPhoneValidation(t).regex, getPhoneValidation(t).errorMessage)
      .when(getPhoneValidation(t).when),
    mapping_sale_id: yup
      .object()
      .nullable()
      .required(t("form.validate.select", { title: "Sale" })),
  });

  const mainForm = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: getInitDefaultValues(),
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isSubmitting },
    control,
    setValue,
    getValues,
    setError,
    register,
    unregister,
    trigger,
    watch,
  } = mainForm;

  const watchedProvince = watch("province_code");
  const watchedDistrict = watch("district_code");
  // Address hooks với record_status dựa trên level
  const { data: provinces, refetch: refetchProvinces } = useProvinces({
    ...provinceParams,
    record_status: is_level_2 ? "O" : "C",
  });

  const { data: districts, refetch: refetchDistricts } = useDistricts({
    ...districtParams,
    record_status: is_level_2 ? "O" : "C",
  });

  const { data: wards, refetch: refetchWards } = useWards({
    ...wardParams,
    record_status: is_level_2 ? "O" : "C",
  });

  useEffect(() => {
    if (provinceParams?.type === 1) refetchProvinces();
  }, [provinceParams, refetchProvinces, is_level_2]);

  useEffect(() => {
    if (districtParams?.type === 2) refetchDistricts();
  }, [districtParams, refetchDistricts, is_level_2]);

  useEffect(() => {
    if (wardParams?.type === 3 && !is_level_2) refetchWards();
  }, [wardParams, refetchWards, is_level_2]);

  // Convert data to options
  const [provinceMenu, setProvinceMenu] = useState(undefined);
  const [districtMenu, setDistrictMenu] = useState(undefined);
  const [wardMenu, setWardMenu] = useState(undefined);

  useEffect(() => {
    if (provinces) {
      const provincesValue = provinces && Object.keys(provinces);
      setProvinceMenu(
        provincesValue.map(item => {
          return { label: provinces?.[item], value: item };
        })
      );
    }
  }, [provinces]);

  useEffect(() => {
    if (districts) {
      const districtsValue = districts && Object.keys(districts);
      setDistrictMenu(
        districtsValue.map(item => {
          return { label: districts?.[item], value: item };
        })
      );
    }
  }, [districts]);

  useEffect(() => {
    if (wards) {
      const wardsValue = wards && Object.keys(wards);
      setWardMenu(
        wardsValue.map(item => {
          return { label: wards?.[item], value: item };
        })
      );
    }
  }, [wards]);

  // Sale logic
  const [saleParams, setSaleParams] = useState({
    sizePerPage: 30,
    columnFilter: {
      "filter[record_status]": "O",
      "filter[user_type]": 3,
      "filter[id]": undefined,
    },
    expand: " ",
    page: 1,
    fields: "",
    run: false,
  });

  const { data: saleList, refetch: fetchListSale } = useListUserProfile(
    { params: saleParams },
    null,
    null,
    "sales-user"
  );

  const pendingSalePromiseRef = useRef(null);

  useEffect(() => {
    if (saleParams.run) {
      fetchListSale();
    }
  }, [saleParams, fetchListSale]);

  useEffect(() => {
    if (pendingSalePromiseRef.current && saleList) {
      const items = saleList.items || [];
      const meta = saleList.meta || {};

      const options = items.map(i => ({
        value: i.id,
        label: `${i.email} - ${i.display_name}`,
      }));
      const hasMore = meta.currentPage < meta.pageCount;
      const { resolve, page } = pendingSalePromiseRef.current;
      resolve({
        options,
        hasMore,
        additional: { page: page + 1 },
      });
      pendingSalePromiseRef.current = null;
    }
  }, [saleList, pendingSalePromiseRef.current]);

  const loadSaleOptions = (searchQuery, loadedOptions, additional) => {
    return new Promise((resolve, reject) => {
      pendingSalePromiseRef.current = {
        resolve,
        page: additional.page,
        searchQuery,
      };
      setSaleParams(prev => ({
        ...prev,
        page: additional.page,
        columnFilter: {
          ...prev.columnFilter,
          "filter[id]": undefined,
        },
        keywords: searchQuery,
        run: true,
      }));
    });
  };

  // Address handlers
  const handleChangeProvince = item => {
    setValue("province_code", item);
    setValue("district_code", null);
    setValue("ward_code", null);
    changeProvince(item?.value);
  };

  const handleChangeDistrict = item => {
    setValue("district_code", item);
    setValue("ward_code", null);
    if (!is_level_2) {
      changeDistrict(item?.value);
    }
  };

  const changeProvince = value => {
    setDistrictParams(prev => ({
      ...prev,
      type: 2,
      parent_code: value,
    }));
  };

  const changeDistrict = value => {
    setWardParams(prev => ({
      ...prev,
      type: 3,
      parent_code: value,
    }));
  };

  const handleChangeIsLevel2 = value => {
    setIsLevel2(value);
    setValue("province_code", null);
    setValue("district_code", null);
    setValue("ward_code", null);
    // setValue("hub_id", null, { shouldDirty: true, shouldTouch: true });
  };

  // Create Bus
  const onSuccessCreateBus = _data => {
    navigate("/bus/list");
  };

  const onErrorCreateBus = error => {
    if (error.code === 422 && error.fields.length > 0) {
      error.fields.forEach(item => {
        let fieldName = item.field;
        switch (item.field) {
          case "common":
            addToast(item.message, {
              appearance: "error",
              autoDismiss: true,
            });
            break;
          default:
            break;
        }
        setError(fieldName, {
          type: "manual",
          message: formConvertErrorMessage(item.message, t),
        });
      });
    }
  };

  const { isLoading, mutate: createBus } = useCreateBus(
    onSuccessCreateBus,
    onErrorCreateBus
  );

  const onSubmit = values => {
    const data = getValues();
    createBus({
      hub_id: data?.hub_id?.value,
      name: data.name,
      phone_number: data.phone_number,
      address: data.address,
      ward_code: data.ward_code?.value || null,
      district_code: data.district_code.value,
      province_code: data.province_code.value,
      mapping_sale_id: data.mapping_sale_id?.value,
    });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{t("title.create")}</title>
        </MetaTags>
        <div className="container-fluid">
          <BreadcrumbWithLink
            title={t("title.create")}
            breadcrumbItems={[
              { name: t("title.manager") },
              { name: t("title.list"), link: "/bus/list" },
              { name: t("title.create") },
            ]}
          />

          <Row className="row-cols-1">
            <Col>
              <Card>
                <CardBody>
                  <Row className="mb-3">
                    <Col md={6}>
                      <FormGroup>
                        <Label
                          className="d-flex align-items-center gap-1"
                          style={{ cursor: "pointer" }}
                          htmlFor="is_level_2"
                          onClick={() => handleChangeIsLevel2(!is_level_2)}
                        >
                          <Switch
                            checked={is_level_2}
                            onChange={handleChangeIsLevel2}
                          />
                          Địa chỉ 2 cấp
                        </Label>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="row-cols-1 row-cols-md-2">
                    <Col>
                      <FormHubSelect
                        // key={`hub-${is_level_2 ? 2 : 3}`}
                        isMyHubOnly
                        isRequired
                        mainForm={mainForm}
                        fieldName="hub_id"
                        t={t}
                        title={t("form.hub_id")}
                        addressLevel={is_level_2 ? 2 : 3}
                        provinceCode={watchedProvince?.value}
                        districtCode={watchedDistrict?.value}
                        onBlur={() => {
                          const currentValue = getValues("hub_id");
                          if (currentValue) {
                            trigger("hub_id");
                          }
                        }}
                      />
                    </Col>

                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="name">
                          {t("form.name")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <input
                          type="text"
                          className={`form-control ${formValidate(
                            errors,
                            touchedFields,
                            "name"
                          )}`}
                          placeholder={t("placeholder.enter", {
                            title: t("form.name"),
                          })}
                          {...register("name")}
                        />
                        {errors.name && (
                          <MessageFormValidate error={errors.name.message} />
                        )}
                      </FormGroup>
                    </Col>

                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="phone_number">
                          {t("form.phone_number")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <input
                          type="text"
                          className={`form-control ${formValidate(
                            errors,
                            touchedFields,
                            "phone_number"
                          )}`}
                          placeholder={t("placeholder.enter", {
                            title: t("form.phone_number"),
                          })}
                          {...register("phone_number")}
                        />
                        {errors.phone_number && (
                          <MessageFormValidate
                            error={errors.phone_number.message}
                          />
                        )}
                      </FormGroup>
                    </Col>

                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="mapping_sale_id">
                          Sale <span className="text-danger">*</span>
                        </Label>
                        <Controller
                          control={control}
                          name="mapping_sale_id"
                          render={({ field }) => (
                            <AsyncPaginate
                              {...field}
                              isClearable
                              debounceTimeout={500}
                              isSearchable
                              placeholder="Chọn sale"
                              loadOptions={loadSaleOptions}
                              additional={{ page: 1 }}
                            />
                          )}
                        />
                        {errors.mapping_sale_id && (
                          <MessageFormValidate
                            error={errors.mapping_sale_id.message}
                          />
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="province">
                          {t("form.province_code")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <Controller
                          control={control}
                          name="province_code"
                          render={({ field }) => (
                            <Select
                              {...field}
                              placeholder={t("placeholder.select", {
                                title: t("form.province_code"),
                              })}
                              options={provinceMenu}
                              isSearchable
                              className="basic-single w-100"
                              onChange={handleChangeProvince}
                            />
                          )}
                        />
                        {errors.province_code && (
                          <MessageFormValidate
                            error={errors.province_code.message}
                          />
                        )}
                      </FormGroup>
                    </Col>

                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="district">
                          {is_level_2 ? "Phường xã" : "Quận huyện"}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <Controller
                          control={control}
                          name="district_code"
                          render={({ field }) => (
                            <Select
                              {...field}
                              placeholder={t("placeholder.select", {
                                title: is_level_2 ? "Phường xã" : "Quận huyện",
                              })}
                              options={districtMenu}
                              isSearchable
                              className="basic-single w-100"
                              isDisabled={!getValues("province_code")}
                              onChange={handleChangeDistrict}
                            />
                          )}
                        />
                        {errors.district_code && (
                          <MessageFormValidate
                            error={errors.district_code.message}
                          />
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    {!is_level_2 && (
                      <Col>
                        <FormGroup className="mb-3">
                          <Label htmlFor="ward">
                            {t("form.ward_code")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Controller
                            control={control}
                            name="ward_code"
                            render={({ field }) => (
                              <Select
                                {...field}
                                placeholder={t("placeholder.select", {
                                  title: t("form.ward_code"),
                                })}
                                options={wardMenu}
                                isSearchable
                                isDisabled={!getValues("district_code")}
                                className="basic-single w-100"
                              />
                            )}
                          />
                          {errors.ward_code && (
                            <MessageFormValidate
                              error={errors.ward_code.message}
                            />
                          )}
                        </FormGroup>
                      </Col>
                    )}

                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="address">
                          {t("form.address")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <input
                          type="text"
                          className={`form-control ${formValidate(
                            errors,
                            touchedFields,
                            "address"
                          )}`}
                          placeholder={t("placeholder.enter", {
                            title: t("form.address"),
                          })}
                          {...register("address")}
                        />
                        {errors.address && (
                          <MessageFormValidate error={errors.address.message} />
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="justify-content-end">
                    <Col className="col-auto">
                      <Link to="/bus/list" className="btn btn-danger btn-label">
                        <i className="fas fa-undo label-icon"></i>
                        {t("form.cancel")}
                      </Link>
                    </Col>

                    <Col className="col-auto">
                      <Button
                        color="primary"
                        className="btn-label"
                        type="button"
                        onClick={() => {
                          handleSubmit(onSubmit)();
                        }}
                        disabled={isLoading}
                      >
                        <i className="bx bx-save label-icon"></i>
                        {t("form.save")}
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CreateBus;
